import {
  useWSSnackbar,
  WSActions,
  WSCard,
  WSGrid,
  WSList
} from "@wingspanhq/fe-component-library";
import {
  IRequirementDefinitionResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import React from "react";

import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { useUserId } from "../../../../../query/hooks/helpers";
import { DataItem } from "../../../../../shared/components/DataItem";
import { RequirementInstructionsView } from "../../../../Requirements/components/RequirementInstructionsView/RequirementInstructionsView";
import { useConfirmCreateRequirementModal } from "../../../components/CreateRequirementDefinitionForm/useConfirmCreateRequirementModal";
import { useCreateRequirementDefinitionSettings } from "../../../hooks/useCreateRequirementDefinitionSettings";
import { useSignatureCost } from "../../../hooks/useSignatureCost";
import { RequirementDefinitionFormValues } from "../../../types";
import { InsuranceInstructionsView } from "../components/InsuranceInstructionsView";
import { RequirementDefinitionTemplate } from "../types";

type Props = {
  onBack: () => void;
  onSuccess: (response: IRequirementDefinitionResponse) => void;
  formData: RequirementDefinitionFormValues;
  template: RequirementDefinitionTemplate;
  engagementId?: string;
};

export const StepReviewAndCreate: React.FC<Props> = ({
  template,
  engagementId,
  onSuccess,
  onBack,
  formData
}) => {
  const { openSnackbar } = useWSSnackbar();
  const confirmCreateRequirementModal = useConfirmCreateRequirementModal();

  const successMessage = () => {
    template.form?.successCreationMessage &&
      openSnackbar({
        message: template.form?.successCreationMessage,
        theme: "dark",
        type: "success"
      });
  };

  const { name } = formData;
  const acceptWingspanMasterPolicy =
    "acceptWingspanMasterPolicy" in formData
      ? formData.acceptWingspanMasterPolicy
      : undefined;

  const clientId = useUserId();

  const { errors, submit, isLoading } =
    useCreateRequirementDefinitionSettings();

  const signatureCost = useSignatureCost();
  const cost =
    template.requirementType === RequirementType.Signature && signatureCost
      ? signatureCost.perSignature
      : undefined;

  return (
    <WSList gap="2XL">
      <WSCard header="Requirement preview">
        <WSGrid gutter={{ m: "2XL" }}>
          <WSGrid.Item span={{ m: "6" }}>
            <DataItem label="Name" value={name} />
          </WSGrid.Item>

          <WSGrid.Item span={{ m: "6" }}>
            <DataItem
              icon={template.icon}
              label="Type"
              secondaryText={template.helperText}
              value={template.name}
            />
          </WSGrid.Item>

          {cost ? (
            <WSGrid.Item span={{ m: "6" }}>
              <DataItem label="Cost" value={cost} />
            </WSGrid.Item>
          ) : null}
        </WSGrid>
      </WSCard>

      {template.instructions || formData.instructions || template.isPolicy ? (
        <WSCard
          header={{
            label: {
              helperText:
                "The following instructions will be shown to the contractors.",
              text: "Instructions to contractors"
            }
          }}
        >
          <WSList gap="2XL">
            {template.isPolicy ? (
              <InsuranceInstructionsView
                allowMasterPolicy={acceptWingspanMasterPolicy}
                insuranceName={template.name}
                policyTypes={template.policyType}
              />
            ) : null}
            {!template.isPolicy &&
            (template.instructions || formData.instructions) ? (
              <WSCard>
                <RequirementInstructionsView
                  instructions={template.instructions || formData.instructions!}
                />
              </WSCard>
            ) : null}
          </WSList>
        </WSCard>
      ) : null}

      <WSErrorMessage contextKey="Other" error={errors} />

      <WSActions
        alignment="fill"
        buttons={[
          {
            kind: "Tertiary",
            label: "Back",
            onClick: onBack,
            type: "button"
          },
          {
            label: "Create requirement",
            loading: isLoading,
            onAsyncClick: async () => {
              const response = await submit({ formData, template });

              confirmCreateRequirementModal.open({
                ...response,
                clientId,
                engagementId,
                onSuccess(requirement) {
                  successMessage();
                  onSuccess?.(requirement);
                }
              });
            },
            type: "submit"
          }
        ]}
      />
    </WSList>
  );
};
